// HOOKS
import { createContext, useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import useQuery from "../../hooks/other/useQuery.other.hook";
// GRAPHQL
import { GET_PART_ORDERS } from "../../graphql/queries/parts/partOrders.queries";
import {
  UPDATE_PART_ORDER,
  DELETE_MANY_PART_ORDERS,
} from "../../graphql/mutations/parts/partOrder.part.mutation";
// CONSTANTS
import OPEN_PART_STATUSES from "../../constants/parts/openPartStatuses.part.constant";

const DEFAULT_QUERY = {
  page: 1,
  limit: 25,
  sort: "-requested_date",
  search: "",
};

const DEFAULT_STATUSES = OPEN_PART_STATUSES.filter(
  (status) => status.checked
).map((status) => status.value);

export const OpenPartOrdersContext = createContext({
  partOrders: [],
  metaData: {},
  isLoading: false,
  query: {},
  partOrderInput: {}, // Used for graphql query
  setPartOrderInput: () => {},
  setMetaData: () => {},
  setIsLoading: () => {},
  setQuery: () => {},
  setPartOrders: () => {},
  fetchPartOrders: () => {},
  updatePartOrder: () => {},
  deletePartOrders: () => {},
  createPartOrder: () => {},
  handleLimitChange: () => {},
  handlePageChange: () => {},
  handleSearch: () => {},
  handleSort: () => {},
});

export const OpenPartOrdersProvider = ({ children }) => {
  const {
    query,
    setQuery,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
  } = useQuery({ defaultQuery: DEFAULT_QUERY });
  // STATE
  const [partOrders, setPartOrders] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [partOrderInput, setPartOrderInput] = useState({
    status: DEFAULT_STATUSES,
  });
  // QUERIES
  const [getPartOrders] = useLazyQuery(GET_PART_ORDERS, {
    fetchPolicy: "no-cache",
  });
  // MUTATIONS
  const [updatePartOrderMutation] = useMutation(UPDATE_PART_ORDER);
  const [deleteManyPartOrdersMutation] = useMutation(DELETE_MANY_PART_ORDERS);
  // EFFECTS
  // Set page to last page if current page is greater than total pages
  useEffect(() => {
    if (metaData.totalPages < query.page) setQuery({ ...query, page: 1 });
  }, [metaData]);

  // Fetch partOrders on query change
  useEffect(() => {
    fetchPartOrders();
  }, [query, partOrderInput]);
  // METHODS
  // Fetches partOrders based on query and partOrderInput
  const fetchPartOrders = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await getPartOrders({
        variables: {
          query: query || {},
          partOrder: partOrderInput || {},
        },
      });
      // Gets rid of error that occurs when reloading a page that had all partOrders deleted
      if (data?.partOrders?.metaData.totalPages < query.page) {
        setIsLoading(false);
        setQuery({ ...query, page: 1 });
        return;
      }
      if (data?.partOrders?.partOrders) {
        setPartOrders(data.partOrders.partOrders);
        setMetaData(data.partOrders.metaData);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };
  // Updates single partOrder
  const updatePartOrder = async (partOrder) => {
    try {
      // Remove __typename from partOrder object and customer object
      partOrder = Object.entries(partOrder).reduce((acc, [key, value]) => {
        if (key === "customer") {
          const customer = Object.entries(value).reduce((acc, [key, value]) => {
            if (key !== "__typename") return { ...acc, [key]: value };
            return acc;
          }, {});
          return { ...acc, customer };
        }
        if (key !== "__typename") return { ...acc, [key]: value };
        return acc;
      }, {});
      const { data, error } = await updatePartOrderMutation({
        variables: {
          partOrder: partOrder,
        },
      });
      return data.updatePartOrder.partOrder;
    } catch (error) {
      console.error(error);
    }
  };

  // Deletes many partOrders
  const deletePartOrders = (selectedPartOrders) => {
    deleteManyPartOrdersMutation({
      variables: {
        partOrders: selectedPartOrders.map((partOrder) => ({
          _id: partOrder._id,
        })),
      },
    });
    fetchPartOrders();
  };
  // Create partOrder
  const createPartOrder = () => {};
  return (
    <OpenPartOrdersContext.Provider
      value={{
        partOrders,
        metaData,
        isLoading,
        query,
        partOrderInput,
        setPartOrderInput,
        setQuery,
        setPartOrders,
        fetchPartOrders,
        updatePartOrder,
        deletePartOrders,
        createPartOrder,
        handleLimitChange,
        handlePageChange,
        handleSearch,
        handleSort,
      }}
    >
      {children}
    </OpenPartOrdersContext.Provider>
  );
};
